/**
 * The company's own bill from Paelode (OPEN-212 PR C, DEC-387 D2): GET/POST
 * /v1/my-paelode-bill/*. VIEW_PLATFORM_BILL (tenant admin and the billing roles).
 */
import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import {
  apiErrorFromResponse,
  authedFetch,
  useApiMutation,
  useApiOpts,
  useApiQuery,
} from '@lodex-pro/web-shared';

const BASE = '/v1/my-paelode-bill';
export const PAELODE_BILL_KEY = ['billing', 'paelode-bill'] as const;

export interface PaelodeInvoice {
  readonly id: string;
  readonly invoiceNumber: string | null;
  readonly status: 'SENT' | 'PAID' | 'VOID';
  readonly issueDate: string | null;
  readonly dueDate: string | null;
  readonly periodStart: string;
  readonly periodEnd?: string;
  readonly totalCents: number;
  readonly amountPaidCents: number;
  readonly balanceCents: number;
  readonly paidOn: string | null;
  /** A bank payment settling (not yet paid). */
  readonly pendingCents: number;
  readonly hasPdf: boolean;
}

export function usePaelodeInvoices(): ReturnType<
  typeof useApiQuery<{ invoices: readonly PaelodeInvoice[] }>
> {
  const opts = useApiOpts();
  return useApiQuery<{ invoices: readonly PaelodeInvoice[] }>(opts, `${BASE}/invoices`, {
    queryKey: [...PAELODE_BILL_KEY, 'list'],
  });
}

export function usePaelodeInvoice(
  id: string,
): ReturnType<typeof useApiQuery<{ invoice: PaelodeInvoice; canPayOnline: boolean }>> {
  const opts = useApiOpts();
  return useApiQuery<{ invoice: PaelodeInvoice; canPayOnline: boolean }>(
    opts,
    `${BASE}/invoices/${id}`,
    { queryKey: [...PAELODE_BILL_KEY, id] },
  );
}

/** A short-lived link to the invoice PDF: a GET on click, never on mount (as useInvoicePdf). */
export function usePaelodeInvoicePdf(
  id: string,
): UseMutationResult<{ url: string; filename: string }, Error, void> {
  const opts = useApiOpts();
  return useMutation<{ url: string; filename: string }, Error, void>({
    mutationFn: async () => {
      const path = `${BASE}/invoices/${id}/pdf`;
      const res = await authedFetch(opts, path);
      if (!res.ok) throw await apiErrorFromResponse(res, path);
      return (await res.json()) as { url: string; filename: string };
    },
  });
}

export function usePaelodePaySession(
  id: string,
): ReturnType<typeof useApiMutation<Record<string, never>, { url: string; expiresAt: string }>> {
  const opts = useApiOpts();
  return useApiMutation<Record<string, never>, { url: string; expiresAt: string }>(
    opts,
    `${BASE}/invoices/${id}/pay-session`,
    'POST',
  );
}
